// frontend/modules/sms/stores/sms-store.ts
import { create } from 'zustand';
import {FakerSmsLogDTO} from "@/shared/api/model";

interface SmsRecipient {
    phoneNumber: string;
    valid: boolean;
}

interface SmsState {
    // SMS form
    message: string;
    recipients: SmsRecipient[];
    senderId: string;
    reference: string;
    isMultiMode: boolean;
    isSending: boolean;

    // Balance
    smsBalance: number | null;
    isLoadingBalance: boolean;

    // Faker SMS logs
    fakerLogs: FakerSmsLogDTO[];
    fakerLogsPage: number;
    fakerLogsPageSize: number;
    fakerLogsTotalElements: number;
    isLoadingFakerLogs: boolean;

    // Actions
    setMessage: (message: string) => void;
    setSenderId: (senderId: string) => void;
    setReference: (reference: string) => void;
    setIsMultiMode: (isMultiMode: boolean) => void;
    addRecipient: (phoneNumber: string) => void;
    removeRecipient: (index: number) => void;
    updateRecipient: (index: number, phoneNumber: string) => void;
    clearForm: () => void;
    setIsSending: (isSending: boolean) => void;
    setSmsBalance: (balance: number) => void;
    setIsLoadingBalance: (isLoading: boolean) => void;
    setFakerLogs: (logs: FakerSmsLogDTO[]) => void;
    setFakerLogsPage: (page: number) => void;
    setFakerLogsPageSize: (size: number) => void;
    setFakerLogsTotalElements: (total: number) => void;
    setIsLoadingFakerLogs: (loading: boolean) => void;
}

export const useSmsStore = create<SmsState>((set) => ({
    // SMS form
    message: '',
    recipients: [{ phoneNumber: '', valid: false }],
    senderId: '',
    reference: '',
    isMultiMode: false,
    isSending: false,

    // Balance
    smsBalance: null,
    isLoadingBalance: false,

    // Faker SMS logs
    fakerLogs: [],
    fakerLogsPage: 0,
    fakerLogsPageSize: 20,
    fakerLogsTotalElements: 0,
    isLoadingFakerLogs: false,

    // Actions
    setMessage: (message) => set({ message }),
    setSenderId: (senderId) => set({ senderId }),
    setReference: (reference) => set({ reference }),
    setIsMultiMode: (isMultiMode) => set({ isMultiMode }),
    addRecipient: (phoneNumber) => set((state) => ({
        recipients: [...state.recipients, { phoneNumber, valid: validatePhoneNumber(phoneNumber) }]
    })),
    removeRecipient: (index) => set((state) => ({
        recipients: state.recipients.filter((_, i) => i !== index)
    })),
    updateRecipient: (index, phoneNumber) => set((state) => {
        const newRecipients = [...state.recipients];
        newRecipients[index] = {
            phoneNumber,
            valid: validatePhoneNumber(phoneNumber)
        };
        return { recipients: newRecipients };
    }),
    clearForm: () => set({
        message: '',
        recipients: [{ phoneNumber: '', valid: false }],
        reference: '',
        isMultiMode: false
    }),
    setIsSending: (isSending) => set({ isSending }),
    setSmsBalance: (balance) => set({ smsBalance: balance }),
    setIsLoadingBalance: (isLoadingBalance) => set({ isLoadingBalance }),
    setFakerLogs: (fakerLogs) => set({ fakerLogs }),
    setFakerLogsPage: (fakerLogsPage) => set({ fakerLogsPage }),
    setFakerLogsPageSize: (fakerLogsPageSize) => set({ fakerLogsPageSize }),
    setFakerLogsTotalElements: (fakerLogsTotalElements) => set({ fakerLogsTotalElements }),
    setIsLoadingFakerLogs: (isLoadingFakerLogs) => set({ isLoadingFakerLogs }),
}));

// Helper function to validate phone numbers
function validatePhoneNumber(phoneNumber: string): boolean {
    // Basic validation for Tanzanian phone numbers
    return /^255[67]\d{8}$/.test(phoneNumber);
}